import { cast, flow, getSnapshot, SnapshotIn, toGenerator, types } from 'mobx-state-tree'

import { FetchModel } from '../models/FetchModel'
import {
  ExistingPartNumbers,
  ExistingPartNumberEditCloneModel,
  ExistingPartNumbersModel,
  NewPartNumberEditCloneModel,
  PartNumberEditCloneModel,
} from '../models/PartNumbers/PartNumbersModel'
import {
  OperationFailure,
  OperationInProgress,
  OperationNotStarted,
  OperationStatusModel,
  OperationSuccess,
} from '../models/common/OperationStatus'
import { FilterModel } from '../models/common/Filters/FilterModel'
import { FilterType } from '../models/common/Filters/FilterType'
import type { SearchFieldFilter } from '../models/common/Filters/SearchFieldFilterModel'
import { isOversightControl, getDeploymentId } from './rootStore'


const [SEARCH_FILTER_NAME] = ['search']
const PART_NUMBERS_PAGE_FILTERS: SnapshotIn<typeof FilterModel> = {
  fieldFilters: {
    [SEARCH_FILTER_NAME]: { type: FilterType.SEARCH, placeholder: 'Search' },
  },
}


export const PartNumbersStore = FetchModel.named('PartNumbersStore')
  .props({
    // For now, "part numbers" won't contain new unsaved part number. The "new part number" row displayed in the table is just a UI trick, not a real part number.
    // We don't add it since it doesn't behave as a real part number in any way - not adhering to filtering/sorting the table, not displaying part number data, and we can't reference it with selectedPartNumber since it has no Id.
    // Also, we don't count it in the part numbers counter on above the table.
    partNumbers: types.maybeNull(types.array(ExistingPartNumbersModel)),

    selectedPartNumber: types.safeReference(ExistingPartNumbersModel),
    partNumberCopyForEditing: types.maybeNull(PartNumberEditCloneModel),

    filters: types.optional(FilterModel, PART_NUMBERS_PAGE_FILTERS),

    fetchStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    saveStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),

    validationStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
  })
  .views((self) => ({
    get getPartNumbersUrl(): string {
      if (isOversightControl()) {
        return `${self.oversightBaseUrl}part_numbers/?deployment_id=${getDeploymentId()}`
      } else {
        return `${self.baseUrl}part_numbers/`
      }
    },
    specificPartNumberUrl(partId: number): string {
      if (isOversightControl()) {
        return `${self.oversightBaseUrl}part_numbers/${partId}/?deployment_id=${getDeploymentId()}`
      } else {
        return `${self.baseUrl}part_numbers/${partId}/`
      }
    },
    _getFilterByFieldName(fieldName: string) {
      const filter = self.filters.fieldFilters.get(fieldName)
      if (filter === undefined) {
        throw new Error(`filter ${fieldName} must appear`)
      }
      return filter
    },
  }))
  .views((self) => ({
    get searchFilter(): SearchFieldFilter {
      return self._getFilterByFieldName(SEARCH_FILTER_NAME) as SearchFieldFilter
    },
  }))
  .views((self) => ({
    get shownPartNumbers(): Array<ExistingPartNumbers> | null {
      if (self.partNumbers === null) return null

      const searchValue = self.searchFilter.value?.toLowerCase() ?? ''
      return self.partNumbers
        .filter((partNumber) => partNumber.number?.toLowerCase().includes(searchValue) || partNumber.title?.toLowerCase().includes(searchValue))
    },
  }))
  .actions((self) => ({
    _setPartNumbers(partNumbers: SnapshotIn<typeof self.partNumbers>) {
      self.partNumbers = cast(partNumbers)

      // selectedPartNumber is a safeReference, so it will automatically change to undefined if the corresponding user is not in the array.
      // However, we must refresh partNumberCopyForEditing manually.
      if (self.partNumberCopyForEditing !== null && self.selectedPartNumber === undefined) {
        self.partNumberCopyForEditing = null
      }
    },
  }))
  .actions((self) => ({
    clearSelectedPartNumber() {
      self.selectedPartNumber = undefined
      self.partNumberCopyForEditing = null
    },
    trySetSelectedPartNumber(partNumberId: number): boolean {
      if (self.partNumbers === null) {
        console.warn("Can't select part number when no part numbers fetched")
        return false
      }

      const selectedPartNumber = self.partNumbers.find((partNumber) => partNumber.id === partNumberId)
      if (selectedPartNumber === undefined) {
        self.selectedPartNumber = undefined
        self.partNumberCopyForEditing = null
        return false
      }

      self.selectedPartNumber = selectedPartNumber
      self.partNumberCopyForEditing = PartNumberEditCloneModel.create(getSnapshot(selectedPartNumber))

      return true
    },
    createNewPartNumber() {
      if (self.partNumbers === null) {
        console.error("Can't create part number when no part number was fetched")
        throw new Error("Can't create part number when no part number was fetched")
      }
      self.validationStatus = OperationNotStarted()

      self.selectedPartNumber = undefined
      self.partNumberCopyForEditing = NewPartNumberEditCloneModel.create({
        number: '',
        title: '',
      })
    },
  }))
  .views((self) => ({
    get isAddingPartNumber() {
      return NewPartNumberEditCloneModel.is(self.partNumberCopyForEditing)
    },
  }))
  .actions((self) => ({
    fetchAndSetPartNumbers: flow(function* fetchAndSetPartNumbers() {
      self.fetchStatus = OperationInProgress()
      const loadResult = yield* toGenerator(
        self.loadObjectOrError<SnapshotIn<typeof self.partNumbers>>(
          self.getPartNumbersUrl,
          {},
          { fetchOperationName: 'Fetch part numbers' },
        ),
      )
      if (loadResult.error) {
        self.fetchStatus = OperationFailure(loadResult.error)
      } else {
        self._setPartNumbers(loadResult.result)
        self.fetchStatus = OperationSuccess()
      }
    }),
  }))
  .actions((self) => ({
    saveEditedPartNumber: flow(function* saveEditedPartNumber() {
      if (self.partNumberCopyForEditing === null) {
        console.warn('No part number edited, cannot save')
        throw new Error('No part number edited, cannot save it')
      }
      
      // validation on the inputs the user inserts:
      self.validationStatus = self.partNumberCopyForEditing.isValid ? OperationSuccess() : OperationFailure(Error("Part is not valid"))
      
      if (self.validationStatus.isFailure) {
        return false
      }
      
       // validation passed, start saving
      self.saveStatus = OperationInProgress()
     
      const partNumberJson = JSON.stringify(getSnapshot(self.partNumberCopyForEditing))

      const [url, method] = ExistingPartNumberEditCloneModel.is(self.partNumberCopyForEditing)
        ? [self.specificPartNumberUrl(self.partNumberCopyForEditing.id), 'PATCH']
        : [self.getPartNumbersUrl, 'POST']

      const fetchResult = yield* toGenerator(
        self.loadObjectOrError<void>(url, { method, body: partNumberJson }, { fetchOperationName: 'Save Part Number' }),
      )
      if (fetchResult.error) {
        self.saveStatus = OperationFailure(fetchResult.error)
      } else {
        self.saveStatus = OperationSuccess()
        void self
          .fetchAndSetPartNumbers() // refresh part number list
          .then(() => {
            // make a fresh editing clone with part number from `part numbers`, if needed.
            if (
              self.selectedPartNumber !== undefined &&
              ExistingPartNumberEditCloneModel.is(self.partNumberCopyForEditing) &&
              self.selectedPartNumber.id === self.partNumberCopyForEditing.id
            ) {
              self.trySetSelectedPartNumber(self.selectedPartNumber.id)
            }
          })
      }
    }),
  }))
